import { useState, useEffect, useRef, useCallback } from 'react';
import { contactService } from '../../api/contactService';
import { useToast } from '../../components/toast/ToastContext';
import DeletePrompt from '../../components/DeletePrompt';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActionType, Column, MetadataField } from '../../types/types';
import { useGetAllDynamicColumnsQuery, useGetDynamicColumnsQuery, useGetDynamicFiltersQuery, useGetImportDatDetailQuery, useLazyGetContactsQuery } from '../../api/contactApiSlice';
import AMSDynamicListing from '../../components/listing/AMSDynamicListing';
import { getAccessToken } from '../auth/services/auth';
import { useLazyGetOrganizationsQuery } from '../../api/organizationApiSlice';
import { capitalize, startCase, upperCase } from 'lodash';


interface SavedTableParams {
    page: number;
    perPage: number;
    sort?: { field: string; direction: 'ASC' | 'DESC' };
    query?: string;
    filters: Record<string, any>;
    visibleColumns: string[];
    importId?: string;
}

const Records = () => {
    const [contacts, setContacts] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [filterGroups, setFilterGroups] = useState<any[]>([]);
    const { id } = useParams();
    const location = useLocation();
    let { scope } = location.state;
    const selectedScope = scope?.toLowerCase();
    // const STORAGE_KEY = selectedScope === 'contact' || selectedScope === 'relationship' ? `tableConfig_contacts` : `tableConfig_organizations`;
    const STORAGE_KEY = selectedScope === 'contact' || selectedScope === 'relationship' ? `tableConfig_import_contacts` : `tableConfig_import_organizations`;

    const initialMount = useRef(true);
    const showToast = useToast();
    const navigate = useNavigate();
    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [columns, setColumns] = useState<Column[]>([]);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [initialFilters, setInitialFilters] = useState<SavedTableParams | null>(null);
    const [shouldFetchContacts, setShouldFetchContacts] = useState(false);

    const [triggerFetchContacts, { data: contactData, isLoading: contactLoading, isFetching: contactIsFetching }] = useLazyGetContactsQuery();
    const [triggerFetchOrganizations, { data: organizationData, isLoading: organizationLoading, isFetching: organizationIsFetching }] = useLazyGetOrganizationsQuery();

    let callTriggerFetch = selectedScope === 'contact' || selectedScope === 'relationship' ? triggerFetchContacts : triggerFetchOrganizations;
    let callData = selectedScope === 'contact' || selectedScope === 'relationship' ? contactData : organizationData;
    let callLoading = selectedScope === 'contact' || selectedScope === 'relationship' ? contactLoading : organizationLoading;
    let callIsFetching = selectedScope === 'contact' || selectedScope === 'relationship' ? contactIsFetching : organizationIsFetching;
    const isFetchComplete = !callLoading && !callIsFetching && callData !== undefined;

    const { data: dynamicColsData, isLoading: isDynamicColsLoading } = useGetAllDynamicColumnsQuery({ scope: selectedScope === 'contact' || selectedScope === 'relationship' ? 'contact' : 'organization', isStandard: false }, {
        skip: !selectedScope,
    });
    const { data: dynamicFiltersData, isLoading: isDynamicFiltersLoading } = useGetDynamicFiltersQuery(selectedScope === 'contact' || selectedScope === 'relationship' ? 'contact' : 'organization', {
        skip: !selectedScope,
    });

    const { data, isLoading: importLoading, isFetching: importFetching, refetch: refetchImportDetail } = useGetImportDatDetailQuery(id, { refetchOnMountOrArgChange: true });
    const getSavedParams = useCallback((): SavedTableParams | null => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return null;

        try {
            const parsed = JSON.parse(saved);
            if (parsed &&
                typeof parsed.page === 'number' &&
                typeof parsed.perPage === 'number' &&
                (parsed.sort === undefined || (
                    typeof parsed.sort?.field === 'string' &&
                    ['ASC', 'DESC'].includes(parsed.sort?.direction)
                )) &&
                (parsed.query === undefined || typeof parsed.query === 'string') &&
                parsed.filters && typeof parsed.filters === 'object' &&
                Array.isArray(parsed.visibleColumns)
            ) {
                return {
                    page: parsed.page,
                    perPage: parsed.perPage,
                    sort: parsed.sort,
                    query: parsed.query || '',
                    filters: parsed.filters || {},
                    visibleColumns: parsed.visibleColumns || []
                };
            }
            return null;
        } catch {
            return null;
        }
    }, []);

    const isFetchingRef = useRef(false);


    const fetchContacts = useCallback(async (
        params: SavedTableParams,
        onError?: (error: any) => void
    ) => {
        try {
            const result = await callTriggerFetch({
                page: params.page,
                perPage: params.perPage,
                sort: params.sort,
                query: params.query,
                filters: params.filters,
                visibleColumns: params.visibleColumns,
                ...(id && { importId: id })
            }).unwrap();

            return result;
        } catch (err) {
            showToast('Fetch Failed', 'Failed to fetch contacts. Please try again.', 'error');
            if (onError) onError(err);
            return null;
        }
    }, [callTriggerFetch]);
    const saveParams = useCallback((params: SavedTableParams) => {
        const existing = localStorage.getItem(STORAGE_KEY);
        let merged = params;
        if (existing) {
            try {
                const parsed = JSON.parse(existing);
                merged = { ...parsed, ...params };
            } catch { }
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    }, []);

    useEffect(() => {
        if (callData) {
            setContacts(callData.results ?? []);
            setTotalItems(callData.total ?? 0);
        }
    }, [callData]);

    useEffect(() => {
        if (data?.importJob?.meta?.selectedColumns && dynamicColsData) {
            const fieldToLabelMap = new Map(
                dynamicColsData?.map((col: any) => [col.field, col.label])
            );
            const selectedColumns = data?.importJob?.meta?.selectedColumns ?? [];

            const transformed = selectedColumns.map((fieldKey: string) => ({
                id: fieldKey,
                label: fieldToLabelMap.get(fieldKey) || fieldKey, // fallback to fieldKey if no match
                isSortable: false,
            }));

            const scopeBasedId =
                selectedScope === 'contact' || selectedScope === 'relationship'
                    ? 'contact_id'
                    : 'organization_id';

            // Only add ID column if it does NOT already exist
            const idAddedCols = selectedColumns.includes(scopeBasedId)
                ? transformed
                : [
                    { id: scopeBasedId, label: scopeBasedId, isSortable: false },
                    ...transformed,
                ];

            setColumns(idAddedCols);

            const defaultParams: SavedTableParams = {
                page: 1,
                perPage: 10,
                filters: {},
                visibleColumns: idAddedCols?.slice(0, 8).map((col: any) => col?.id), // ✅ slice, not splice
                query: "",
            };
            let initialParams: SavedTableParams;
            setInitialFilters(defaultParams);
            fetchContacts(defaultParams);
        }

    }, [data?.importJob?.meta?.selectedColumns, dynamicColsData, getSavedParams, saveParams]);

    useEffect(() => {
        if (dynamicFiltersData?.length) {
            const fieldsToExclude = ['age', 'diabetes'];
            const filtered = dynamicFiltersData.filter(
                (item: MetadataField) =>
                    !fieldsToExclude.some((excluded) =>
                        item.field.toLowerCase().includes(excluded.toLowerCase())
                    )
            );

            const transformed = filtered.map((col: any) => ({
                key: col.field || '',
                label: col.label || '',
                dataType: col.dataType || '',
                options: col.options ?? [],
            }));

            setFilterGroups(transformed);
        }
    }, [dynamicFiltersData]);

    useEffect(() => {
        if (shouldFetchContacts && initialFilters) {
            if (initialMount.current) {
                initialMount.current = false;
                fetchContacts(initialFilters);
            }
            setShouldFetchContacts(false);
        }
    }, [shouldFetchContacts, initialFilters, fetchContacts]);

    const handleFetchRequested = useCallback((params: SavedTableParams) => {
        saveParams(params);
        setInitialFilters(params);
        if (!params.visibleColumns.length) return;

        fetchContacts(params);
    }, [fetchContacts, saveParams]);


    const exportFiles = async (entity: string) => {
        let newEntity = `${entity}s`
        const savedParams = getSavedParams();
        try {
            const token = getAccessToken(); // adjust if token is from Redux or cookies
            const queryString = new URLSearchParams();
            queryString.append('export', '1');
            if (savedParams?.visibleColumns?.length) {
                let columns = savedParams?.visibleColumns
                    .filter((column: any) => column !== 'contact_id')
                    .join(',');
                queryString.append('listingColumns', columns);
            }
            const apiUrl = `${process.env.REACT_APP_API_URL}/api/${newEntity}?${queryString.toString()}`;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL
                }/api/${newEntity}?${queryString.toString()}&importJobId=${id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                showToast("Error", "Failed to export file", "error");
                return;
            }

            const blob = await response.blob();

            // Extract filename from content-disposition header (optional)
            const contentDisposition = response.headers.get("Content-Disposition");
            const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
            const filename =
                filenameMatch?.[1] ||
                `export-${entity}.xlsx`;

            // Trigger file download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            showToast("Success", "File downloaded successfully", "success");
        } catch (err) {
            console.error(err);
            showToast("Error", "Something went wrong while exporting", "error");
        }
    };






    return (
        <>
            <AMSDynamicListing
                showActions
                routeKey="records"
                scope={selectedScope}
                noDataTitle='No Record Found'
                noDataSubtitle='Your record list is currently empty. Start adding new record to keep everything organized and easy to find.'
                filterGroups={filterGroups}
                title="Import Details"
                subtitle="Here you can see the whole details of your recent imports."
                columns={columns}
                data={contacts}
                totalItems={totalItems}
                loading={callLoading || isDynamicColsLoading || isDynamicFiltersLoading || importLoading}
                isFetching={callIsFetching || isDynamicFiltersLoading || isDynamicColsLoading || importLoading || importFetching}
                isFetchComplete={isFetchComplete}
                onAction={() => { }}
                onFetchRequested={handleFetchRequested}
                noTableAction={true}
                setInitialFilters={setInitialFilters}
                dynamicColumnsListing={true}
                skeletonHeight={'20px'}
                ImportData={data?.importJob || {}}
                onExport={() => exportFiles(selectedScope)}
                fileId={id}
                containerHeight={365}
                hideActionColumn
                refetchImportData={refetchImportDetail}
            />
            {/* {showDeletePrompt && (
        <DeletePrompt onConfirm={confirmDelete} onCancel={cancelDelete} />
      )} */}
        </>
    );
};

export default Records;